import { ChangeDetectionStrategy, Component, Inject, inject } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { TextConstants } from "@shared/constants";
import { IDelivery } from "@shared/interfaces/delivery.interface";
import { EudrDds } from "@shared/interfaces/eudr-dds.interface";
import { AuthenticationService, NotificationService } from "@shared/services";
import { EudrDdsService } from "@shared/services/api/eudr-dds.service";
import { FormUtils } from "@shared/utils";
import { CustomValidators } from "@shared/validators";

@Component({
  standalone: false,
  selector: "app-add-eudr-dds-reference-number-dialog",
  templateUrl: "./add-eudr-dds-identifier-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddEudrDdsIdentifierDialogComponent {
  delivery: IDelivery;

  private readonly fb = inject(FormBuilder);

  private readonly eudrDdsService = inject(EudrDdsService);

  private readonly authenticationService = inject(AuthenticationService);

  private readonly notificationService = inject(NotificationService);

  private readonly activeOrganisationId = this.authenticationService.getActiveOrganisationId();

  readonly formGroup = this.fb.group({
    internalReferenceNumber: [null as string, [CustomValidators.required]],
    ddsIdentifier: [null as string, [CustomValidators.required]],
  });

  constructor(
    public dialogRef: MatDialogRef<AddEudrDdsIdentifierDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      delivery: IDelivery;
    },
  ) {
    this.delivery = this.data.delivery;

    this.formGroup.patchValue({
      internalReferenceNumber: this.delivery.deliveryId,
    });
  }

  readonly translations = {
    title: $localize`:@@action.delivery-overlay.addexistingddsfromtraces:Add existing DDS from TRACES`,
    internalReferenceNumber: $localize`:@@shared.internalreferencenumber:Internal reference number`,
    ddsIdentifier: $localize`:@@delivery-overlay.dds-identifier:DDS identifier`,
    ddsIdentifierTooltip: $localize`:@@delivery-overlay.dds-identifier-tooltip:The unique identifier of the DDS on TRACES (not the Reference Number / Verification Number / Internal Reference Number). Is usually stated as the last part of the DDS URL in the browser (e.g. 6a6561d2-86e4-46fd-9814-6094ab3c64ff).`,
  };

  private async createDdsReference() {
    if (await FormUtils.isInvalid(this.formGroup)) {
      FormUtils.findAndMarkInvalidControls(this.formGroup);

      return null;
    }

    const { internalReferenceNumber, ddsIdentifier } = this.formGroup.value;

    const payload: EudrDds.IPayloadReferenced = {
      deliveryUri: `/organisations/${this.activeOrganisationId}/deliveries/${this.delivery.id}`,
      ddsIdentifier,
      internalReference: internalReferenceNumber,
      type: EudrDds.TypeEnum.EXISTING_DDS_IDENTIFIER,
    };

    return await this.eudrDdsService.createOrUpdate(payload);
  }

  onClose() {
    this.dialogRef.close({ hasSaved: false });
  }

  async onSubmit() {
    try {
      const ddsReference = await this.createDdsReference();

      if (!ddsReference) {
        this.notificationService.showError(TextConstants.FILL_REQUIRED_FIELDS);

        return;
      }

      this.dialogRef.close({ hasSaved: true, ddsReference });
    } catch (error) {
      this.notificationService.showError(error);
    }
  }
}
